import styled from "styled-components";
import { Link } from "react-router-dom";

const Logo = "/logo.png";

const FooterWrap = styled.footer`
  margin-top: auto;
  padding: 82px 20px 60px;
  background:
    radial-gradient(820px at 10% 0%, rgba(245, 158, 11, 0.08), transparent 58%),
    radial-gradient(860px at 90% 0%, rgba(56, 189, 248, 0.08), transparent 60%),
    ${({ theme }) => theme.colors.bg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(300px, 0.8fr);
  gap: 40px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 28px;
    text-align: center;
  }
`;

const BrandColumn = styled.div`
  display: grid;
  gap: 18px;
  min-width: 0;
`;

const BrandTop = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 860px) {
    justify-content: center;
  }
`;

const BrandLink = styled.a`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 42px;
  width: auto;
  object-fit: contain;
  display: block;

  @media (max-width: 768px) {
    height: 36px;
  }
`;

const BrandMeta = styled.div`
  display: grid;
  gap: 4px;
`;

const BrandTitle = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  font-weight: 900;
`;

const BrandSub = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  line-height: 1.6;
`;

const Tagline = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 14px;
  line-height: 1.8;
  max-width: 540px;

  @media (max-width: 860px) {
    max-width: none;
  }
`;

const UtilityBlock = styled.div`
  display: grid;
  gap: 12px;
`;

const BlockLabel = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LinkGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  a {
    color: ${({ theme }) => theme.colors.textSecondary};
    text-decoration: none;
    padding: 9px 12px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: rgba(15, 23, 42, 0.32);

    &:hover {
      color: ${({ theme }) => theme.colors.text};
    }
  }

  @media (max-width: 860px) {
    justify-content: center;
  }
`;

const RightColumn = styled.div`
  display: grid;
  justify-items: end;
  gap: 16px;

  @media (max-width: 860px) {
    justify-items: center;
  }
`;

const PrimaryLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;

  a {
    color: ${({ theme }) => theme.colors.textSecondary};
    text-decoration: none;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: rgba(15, 23, 42, 0.42);
    font-size: 14px;
    font-weight: 700;

    &:hover {
      color: ${({ theme }) => theme.colors.text};
    }
  }

  @media (max-width: 860px) {
    justify-content: center;
  }
`;

const Bottom = styled.div`
  margin-top: 42px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

function Footer() {
  return (
    <FooterWrap>
      <Container>
        <Top>
          <BrandColumn>
            <BrandTop>
              <BrandLink href="/">
                <LogoImage src={Logo} alt="skilll.dev logo" />
              </BrandLink>
              <BrandMeta>
                <BrandTitle>skilll.dev</BrandTitle>
                <BrandSub>Roadmap clarity for serious learners</BrandSub>
              </BrandMeta>
            </BrandTop>

            <Tagline>
              A career operating system for students and freshers.
              <br />
              Built to replace confusion with clarity.
            </Tagline>

            <UtilityBlock>
              <LinkGroup>
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/cookies-policy">Cookies Policy</Link>
                <Link to="/terms-of-service">Terms of Service</Link>
              </LinkGroup>
            </UtilityBlock>
          </BrandColumn>

          <RightColumn>
            <BlockLabel>Explore</BlockLabel>
            <PrimaryLinks>
              <Link to="/support">Support / Report Issue</Link>
              <Link to="/feedback">Rate skilll.dev</Link>
              <Link to="/code-practice">Code Practice</Link>
              <Link to="/sql-practice">SQL Practice</Link>
              <Link to="/json-formatter">JSON Formatter</Link>
              <Link to="/features">Features</Link>
              <Link to="/roadmaps">Roadmaps</Link>
              <Link to="/community/feeds">Community</Link>
              <Link to="/ai-mentors">AI Mentors</Link>
              <Link to="/pricing">Pricing</Link>
            </PrimaryLinks>
          </RightColumn>
        </Top>

        <Bottom>
          skilll.dev. All rights reserved. Built by Engineer Codewala for
          Developers with love & right intention © {new Date().getFullYear()}
        </Bottom>
      </Container>
    </FooterWrap>
  );
}

export default Footer;
